// File: /src/hooks/finance/useFinanceSummary.js

import { useMemo } from 'react';
import {
	buildFinancePieData,
	calculateFinanceKPIs,
} from '../../utils/financeMetrics';

const EMPTY_SUMMARY = {
	totalIncome: 0,
	totalExpense: 0,
	totalReceived: 0,
	totalPending: 0,
	totalOverdue: 0,
	netTotal: 0,
};

const parseNumeric = (value) => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : 0;
};

export const ensureSummaryShape = (summary = {}) => {
	const safeSummary = summary || {};
	return {
		...EMPTY_SUMMARY,
		...Object.fromEntries(
			Object.keys(EMPTY_SUMMARY).map((key) => [
				key,
				parseNumeric(safeSummary[key]),
			]),
		),
	};
};

export const withRecomputedNet = (summary = {}) => {
	const safeSummary = summary || {};
	const shaped = ensureSummaryShape(safeSummary);
	return {
		...shaped,
		netTotal:
			typeof safeSummary.netTotal === 'number'
				? safeSummary.netTotal
				: shaped.totalIncome +
					shaped.totalReceived -
					(shaped.totalExpense + shaped.totalOverdue),
	};
};

export const applySummaryDelta = (
	summary = EMPTY_SUMMARY,
	{ incomeDelta = 0, expenseDelta = 0 } = {},
) => {
	const shaped = ensureSummaryShape(summary);
	const updatedSummary = {
		...shaped,
		totalIncome: shaped.totalIncome + incomeDelta,
		totalExpense: shaped.totalExpense + expenseDelta,
	};
	// Remove netTotal to force recomputation with new values
	delete updatedSummary.netTotal;
	return withRecomputedNet(updatedSummary);
};

export const useFinanceSummary = (summary) => {
	const normalizedSummary = useMemo(
		() => withRecomputedNet(summary),
		[summary],
	);

	const totals = useMemo(() => {
		const totalRevenue =
			normalizedSummary.totalIncome + normalizedSummary.totalReceived;
		const totalExpenses = normalizedSummary.totalExpense;
		const pieData = buildFinancePieData(normalizedSummary);
		const kpis = calculateFinanceKPIs(normalizedSummary);
		return {
			totalRevenue,
			totalExpenses,
			pieData,
			kpis,
		};
	}, [normalizedSummary]);

	return {
		summary: normalizedSummary,
		totalRevenue: totals.totalRevenue,
		totalExpenses: totals.totalExpenses,
		pieData: totals.pieData,
		kpis: totals.kpis,
	};
};

export default useFinanceSummary;
